import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Upload, Image as ImageIcon, Search, Loader2 } from 'lucide-react';

interface ThumbnailUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  currentThumbnail?: string | null;
  onSuccess: () => void;
}

interface CroppedArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ThumbnailByLead {
  leadName: string;
  thumbnails: Array<{
    url: string;
    projectId: string;
    projectName: string;
  }>;
}

interface ThumbnailByClient {
  url: string;
  projectId: string;
  projectName: string;
}

async function getCroppedImg(imageSrc: string, pixelCrop: CroppedArea): Promise<Blob> {
  const image = new Image();
  image.src = imageSrc;
  
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      },
      'image/png'
    );
  });
}

export function ThumbnailUploadDialog({
  open,
  onOpenChange,
  projectId,
  currentThumbnail,
  onSuccess,
}: ThumbnailUploadDialogProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedArea | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientCode, setClientCode] = useState('');
  const { toast } = useToast();

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: CroppedArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size before processing (20MB limit)
      if (file.size > 20 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "File size must be less than 20MB",
          variant: "destructive"
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      // Validate file size before processing (20MB limit)
      if (file.size > 20 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "File size must be less than 20MB",
          variant: "destructive"
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      toast({
        title: 'Error',
        description: 'Please select and crop an image first',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);

    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

      const resizedBlob = await imageCompression(croppedBlob as File, {
        maxWidthOrHeight: 152,
        useWebWorker: true,
        fileType: 'image/webp',
        initialQuality: 0.8,
      });

      const canvas = document.createElement('canvas');
      canvas.width = 152;
      canvas.height = 75;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      const img = new Image();
      const imgUrl = URL.createObjectURL(resizedBlob);
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imgUrl;
      });

      ctx.drawImage(img, 0, 0, 152, 75);
      URL.revokeObjectURL(imgUrl);

      // Send the WebP as a binary multipart upload (NOT a base64 data URI in a
      // JSON body) — the production edge/WAF rejects long base64 `data:` strings
      // in request bodies with a 403, aborting the request before it reaches the
      // server.
      const webpBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode thumbnail'))),
          'image/webp',
          0.8,
        );
      });

      const formData = new FormData();
      formData.append('thumbnail', webpBlob, 'thumbnail.webp');

      await apiRequest('PATCH', `/api/projects/${projectId}/thumbnail`, formData);

      toast({
        title: 'Success',
        description: 'Thumbnail uploaded successfully',
      });

      onSuccess();
      onOpenChange(false);
      
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload thumbnail',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onOpenChange(false);
  };

  // Fetch thumbnails grouped by lead artist for "From History" tab
  const { data: thumbnailsByLead, isLoading: isLoadingHistory } = useQuery<ThumbnailByLead[]>({
    queryKey: ['/api/projects/thumbnails/by-lead'],
    enabled: open,
  });

  // Fetch thumbnails by client code for "By Client" tab
  const trimmedClientCode = clientCode.toUpperCase().trim();
  const isValidClientCode = /^[A-Z]{2}$/.test(trimmedClientCode);
  const { data: thumbnailsByClient, isLoading: isLoadingByClient, isFetching: isFetchingByClient } = useQuery<ThumbnailByClient[]>({
    queryKey: [`/api/projects/thumbnails/by-client?clientId=${trimmedClientCode}`],
    enabled: open && isValidClientCode,
  });

  const handleSelectFromHistory = async (sourceProjectId: string) => {
    setIsProcessing(true);
    try {
      // Copy the source project's thumbnail server-side — send only the id, never
      // the base64 image, so the request body stays small (avoids the edge/WAF
      // 403 that long base64 `data:` bodies trigger in production).
      await apiRequest('POST', `/api/projects/${projectId}/thumbnail/copy-from`, {
        sourceProjectId,
      });

      toast({
        title: 'Success',
        description: 'Thumbnail applied from history',
      });

      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error applying thumbnail:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to apply thumbnail',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} data-testid="thumbnail-upload-dialog">
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogTitle>Project Thumbnail</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="upload" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="upload" className="gap-2">
              <Upload className="h-4 w-4" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <ImageIcon className="h-4 w-4" />
              History
            </TabsTrigger>
            <TabsTrigger value="client" className="gap-2">
              <Search className="h-4 w-4" />
              By Client
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="flex-1 mt-4">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload an image and select the area for your thumbnail (152x75 pixels)
              </p>

              {!imageSrc ? (
                <div
                  className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-12 text-center hover:border-muted-foreground/50 transition-colors cursor-pointer"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('thumbnail-file-input')?.click()}
                >
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    Drag and drop an image here, or click to select
                  </p>
                  <input
                    id="thumbnail-file-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                    data-testid="thumbnail-file-input"
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative h-96 bg-muted rounded-lg overflow-hidden">
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={152 / 75}
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Zoom</label>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => setImageSrc(null)}
                    className="w-full"
                  >
                    Choose Different Image
                  </Button>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isProcessing}
                  data-testid="cancel-thumbnail-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!imageSrc || isProcessing}
                  data-testid="save-thumbnail-button"
                >
                  {isProcessing ? 'Processing...' : 'Save Thumbnail'}
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-4 data-[state=active]:flex data-[state=active]:flex-col">
            <p className="text-sm text-muted-foreground mb-4">
              Select a thumbnail from existing projects
            </p>

            {isLoadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : !thumbnailsByLead || thumbnailsByLead.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No existing thumbnails found
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <ScrollArea className="h-[400px]">
                  <div className="space-y-6 p-4">
                    {thumbnailsByLead.map((group) => (
                      <div key={group.leadName}>
                        <h4 className="text-sm font-semibold text-foreground mb-3">
                          {group.leadName}
                        </h4>
                        <div className="grid grid-cols-4 gap-2">
                          {group.thumbnails.map((thumb) => (
                            <button
                              key={thumb.projectId}
                              onClick={() => handleSelectFromHistory(thumb.projectId)}
                              disabled={isProcessing || thumb.projectId === projectId}
                              className="group relative aspect-[152/75] rounded-md overflow-hidden border-2 border-transparent hover:border-primary focus:border-primary focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                              title={thumb.projectName}
                            >
                              <img
                                src={thumb.url}
                                alt={thumb.projectName}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <span className="text-[10px] text-white text-center px-1 line-clamp-2">
                                  {thumb.projectName}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                Cancel
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="client" className="mt-4 data-[state=active]:flex data-[state=active]:flex-col">
            <p className="text-sm text-muted-foreground mb-4">
              Search thumbnails by 2-letter client code (e.g., "AD")
            </p>

            <div className="mb-4">
              <Input
                placeholder="Enter client code (e.g., AD)"
                value={clientCode}
                onChange={(e) => setClientCode(e.target.value.slice(0, 2))}
                maxLength={2}
                className="uppercase w-32"
              />
            </div>

            {!isValidClientCode ? (
              <div className="text-center py-12 text-muted-foreground">
                Enter a 2-letter client code to search
              </div>
            ) : isLoadingByClient || isFetchingByClient ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : !thumbnailsByClient || thumbnailsByClient.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No thumbnails found for client "{trimmedClientCode}"
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <ScrollArea className="h-[350px]">
                  <div className="p-4">
                    <p className="text-xs text-muted-foreground mb-3">
                      Found {thumbnailsByClient.length} thumbnail{thumbnailsByClient.length !== 1 ? 's' : ''} for "{trimmedClientCode}"
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {thumbnailsByClient.map((thumb) => (
                        <button
                          key={thumb.projectId}
                          onClick={() => handleSelectFromHistory(thumb.projectId)}
                          disabled={isProcessing || thumb.projectId === projectId}
                          className="group relative aspect-[152/75] rounded-md overflow-hidden border-2 border-transparent hover:border-primary focus:border-primary focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          title={thumb.projectName}
                        >
                          <img
                            src={thumb.url}
                            alt={thumb.projectName}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-[10px] text-white text-center px-1 line-clamp-2">
                              {thumb.projectName}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </ScrollArea>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                Cancel
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
